/**
 * claude_primitives.js — DAS-65 Phase 3 공용 UI 원소
 *
 * ClaudeDesignDashboard/pages/primitives.jsx 에서 v2 포팅에 필요한 요소만 추출.
 * silver 토큰 전용. 하드코딩 색상 금지.
 * 기존 src/assets/ 로고와 중복 정의하지 않는다.
 */

import React from 'react';

/** 육각형 로고 SVG — v2 전용 */
export const HexLogo = ({ size = 18, stroke = 'currentColor', accent = 'var(--color-brand)', strokeWidth = 3.5 }) => (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none" aria-hidden="true">
        <polygon
            points="24,5 41,15 41,33 24,43 7,33 7,15"
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinejoin="round"
            fill="none"
        />
        <path
            d="M15 30 L33 18"
            stroke={accent}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
        />
    </svg>
);

/** 아이콘 모음 — v2 전용 (silver 토큰 기반 currentColor)
 *  DAS-73 Phase 1: 시안(`primitives.jsx::Icon`)과 항목 동기화 — Shell에서 쓰는
 *  bell / search / chevR / chevL / chevD / dashboard / history / backtest /
 *  wallet / maint / user / logout / dot / x / close / hamburger / calendar /
 *  filter / ext / up / down 추가. silver 토큰 기반 currentColor 유지.
 */
export const V2Icon = ({ name, size = 16, className = '' }) => {
    const common = {
        width: size,
        height: size,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 1.8,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        'aria-hidden': true,
        className,
    };
    const paths = {
        // 기존 v2 셋
        eye:     <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></>,
        eyeOff:  <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>,
        check:   <><path d="M20 6L9 17l-5-5"/></>,
        arrowR:  <><path d="M5 12h14M12 5l7 7-7 7"/></>,
        key:     <><circle cx="8" cy="15" r="4"/><path d="M10.85 12.15L19 4"/><path d="M18 5l2 2"/><path d="M15 8l2 2"/></>,
        edit:    <><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4z"/></>,
        copy:    <><rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>,
        trash:   <><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></>,
        plus:    <><path d="M12 5v14M5 12h14"/></>,
        alert:   <><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></>,
        refresh: <><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/></>,
        more:    <><circle cx="5" cy="12" r="1" fill="currentColor"/><circle cx="12" cy="12" r="1" fill="currentColor"/><circle cx="19" cy="12" r="1" fill="currentColor"/></>,
        download:<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></>,

        // DAS-73 Phase 1: Shell 동기화용 추가 아이콘
        dashboard: <><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></>,
        history:   <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
        backtest:  <><path d="M4 4h16v4H4zM4 10h16v4H4zM4 16h16v4H4z"/><path d="M7 6h.01M7 12h.01M7 18h.01"/></>,
        wallet:    <><path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M21 12h-4a2 2 0 0 0 0 4h4"/></>,
        maint:     <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.8-3.8a6 6 0 0 1-7.9 7.9L8 19a2 2 0 1 1-3-3l5.6-5.6a6 6 0 0 1 7.9-7.9l-3.7 3.8z"/></>,
        user:      <><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-6 8-6s8 2 8 6"/></>,
        logout:    <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5"/><path d="M21 12H9"/></>,
        bell:      <><path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8"/><path d="M10 21a2 2 0 0 0 4 0"/></>,
        search:    <><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></>,
        chevL:     <><path d="M15 18l-6-6 6-6"/></>,
        chevR:     <><path d="M9 18l6-6-6-6"/></>,
        chevD:     <><path d="M6 9l6 6 6-6"/></>,
        up:        <><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></>,
        down:      <><path d="M12 5v14"/><path d="M5 12l7 7 7-7"/></>,
        filter:    <><path d="M3 6h18"/><path d="M6 12h12"/><path d="M10 18h4"/></>,
        x:         <><path d="M18 6L6 18M6 6l12 12"/></>,
        close:     <><path d="M18 6L6 18M6 6l12 12"/></>,
        dot:       <><circle cx="12" cy="12" r="3" fill="currentColor"/></>,
        ext:       <><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><path d="M15 3h6v6"/><path d="M10 14L21 3"/></>,
        calendar:  <><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
        hamburger: <><path d="M3 6h18M3 12h18M3 18h18"/></>,
    };
    return <svg {...common}>{paths[name] || null}</svg>;
};

/** 시안 명칭 호환 별칭 — `Icon` 으로도 동일 컴포넌트 노출 (시안 컨벤션 매칭) */
export { V2Icon as Icon };

/** 상태 표시 점 */
export const StatusDot = ({ tone = 'gain', label, size = 7 }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
        <span style={{
            width: size,
            height: size,
            borderRadius: 999,
            background: `var(--color-${tone})`,
            boxShadow: `0 0 8px var(--color-${tone}-subtle)`,
            flexShrink: 0,
        }} />
        {label && (
            <span style={{ fontSize: 12, color: 'var(--color-content-secondary)' }}>{label}</span>
        )}
    </span>
);

/** 거래소 칩 — v2 (silver 토큰 기반, 배경은 표면 토큰 활용) */
export const ExchangeChip = ({ name }) => {
    const map = {
        upbit:   { label: 'UPBIT',   bg: 'var(--color-brand-subtle)',   color: 'var(--color-brand-light)', border: 'var(--color-brand)' },
        binance: { label: 'BINANCE', bg: 'var(--color-warning-subtle)', color: 'var(--color-warning)',     border: 'var(--color-warning)' },
        bybit:   { label: 'BYBIT',   bg: 'var(--color-warning-subtle)', color: 'var(--color-warning)',     border: 'var(--color-warning)' },
        korbit:  { label: 'KORBIT',  bg: 'var(--color-surface-elevated)', color: 'var(--color-content-secondary)', border: 'var(--color-surface-border)' },
        bithumb: { label: 'BITHUMB', bg: 'var(--color-surface-elevated)', color: 'var(--color-content-secondary)', border: 'var(--color-surface-border)' },
    };
    const key = (name || '').toLowerCase();
    const s = map[key] || { label: (name || '').toUpperCase(), bg: 'var(--color-surface-elevated)', color: 'var(--color-content-muted)', border: 'var(--color-surface-border)' };
    return (
        <span style={{
            background: s.bg,
            color: s.color,
            border: `1px solid color-mix(in oklch, ${s.border} 40%, var(--color-surface-border))`,
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.05em',
            fontFamily: 'JetBrains Mono, monospace',
        }}>
            {s.label || name}
        </span>
    );
};

/** KPI 레이블 */
export const KpiLabel = ({ children }) => (
    <div style={{
        fontSize: 12,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'var(--color-content-muted)',
        fontWeight: 500,
        marginBottom: 4,
        fontFamily: 'JetBrains Mono, monospace',
    }}>
        {children}
    </div>
);

/** 배지 — tone 기반 */
export const V2Badge = ({ children, tone = 'neutral' }) => {
    const map = {
        neutral: { bg: 'var(--color-surface-elevated)', color: 'var(--color-content-secondary)', border: 'var(--color-surface-border)' },
        gain:    { bg: 'var(--color-gain-subtle)',      color: 'var(--color-gain)',               border: 'color-mix(in oklch, var(--color-gain) 35%, transparent)' },
        loss:    { bg: 'var(--color-loss-subtle)',      color: 'var(--color-loss)',               border: 'color-mix(in oklch, var(--color-loss) 35%, transparent)' },
        warning: { bg: 'var(--color-warning-subtle)',   color: 'var(--color-warning)',            border: 'color-mix(in oklch, var(--color-warning) 35%, transparent)' },
        brand:   { bg: 'var(--color-brand-subtle)',     color: 'var(--color-brand-light)',        border: 'color-mix(in oklch, var(--color-brand) 40%, transparent)' },
    };
    const t = map[tone] || map.neutral;
    return (
        <span style={{
            background: t.bg,
            color: t.color,
            border: `1px solid ${t.border}`,
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.04em',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontFamily: 'JetBrains Mono, monospace',
            textTransform: 'uppercase',
        }}>
            {children}
        </span>
    );
};

/* ──────────────────────────────────────────────────────────────────────────
 * DAS-102 (Phase 00) — Design Handoff 누락 primitives
 * 시안 mydashboard/project/pages/primitives.jsx 와 시그니처 1:1 매칭.
 * silver 토큰 전용 (var(--color-*) 만 사용, 하드코딩 hex 금지).
 * ────────────────────────────────────────────────────────────────────────── */

/** 버튼 — variant: primary/ghost/outline/danger, size: sm/md/lg */
export const Btn = ({ children, variant = 'primary', icon, size = 'md', style, ...rest }) => {
    const sizes = {
        sm: { h: 28, pad: '0 12px', fs: 12 },
        md: { h: 36, pad: '0 16px', fs: 13 },
        lg: { h: 44, pad: '0 20px', fs: 14 },
    };
    const sz = sizes[size] || sizes.md;
    const variants = {
        primary: {
            background: 'var(--color-content)',
            color: 'var(--color-content-inverse)',
            border: '1px solid var(--color-content)',
        },
        ghost: {
            background: 'transparent',
            color: 'var(--color-content-secondary)',
            border: '1px solid var(--color-surface-border)',
        },
        outline: {
            background: 'var(--color-surface-card)',
            color: 'var(--color-content)',
            border: '1px solid var(--color-surface-border)',
        },
        danger: {
            background: 'var(--color-loss-subtle)',
            color: 'var(--color-loss)',
            border: '1px solid color-mix(in oklch, var(--color-loss) 40%, var(--color-surface-border))',
        },
    };
    const v = variants[variant] || variants.primary;
    return (
        <button {...rest} style={{
            ...v,
            height: sz.h,
            padding: sz.pad,
            fontSize: sz.fs,
            borderRadius: 8,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            letterSpacing: '-0.005em',
            transition: 'all .15s',
            ...style,
        }}>
            {icon ? <V2Icon name={icon} size={sz.fs + 2} /> : null}
            {children}
        </button>
    );
};

/** 카드 컨테이너 — tone: default/elevated/alt */
export const Card = ({ children, padding = 20, style, tone = 'default', ...rest }) => {
    const bg = tone === 'elevated'
        ? 'var(--color-surface-elevated)'
        : tone === 'alt'
            ? 'var(--color-surface-card-alt)'
            : 'var(--color-surface-card)';
    return (
        <div {...rest} style={{
            background: bg,
            border: '1px solid var(--color-surface-border)',
            borderRadius: 16,
            padding,
            ...style,
        }}>
            {children}
        </div>
    );
};

/** KPI 값 표시 — tone: gain/loss/warning/brand 등 / mono 기본 true */
export const KpiValue = ({ children, size = 28, tone, mono = true }) => {
    const color = tone ? `var(--color-${tone})` : 'var(--color-content)';
    return (
        <div style={{
            fontSize: size,
            fontWeight: 600,
            color,
            letterSpacing: '-0.015em',
            fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit',
            lineHeight: 1.05,
        }}>
            {children}
        </div>
    );
};

/** 변동률 표시 — 양수는 gain, 음수는 loss 자동 */
export const Delta = ({ value, mono = true, size = 12 }) => {
    const num = typeof value === 'number' ? value : Number(value);
    const safe = Number.isFinite(num) ? num : 0;
    const pos = safe >= 0;
    const tone = pos ? 'gain' : 'loss';
    const display = safe.toFixed ? safe.toFixed(2) : String(safe);
    return (
        <span style={{
            color: `var(--color-${tone})`,
            fontSize: size,
            fontWeight: 600,
            fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit',
        }}>
            {pos ? '+' : ''}{display}%
        </span>
    );
};

/** 폼 필드 행 — label · children · hint */
export const Field = ({ label, children, hint }) => (
    <label style={{ display: 'block' }}>
        <div style={{
            fontSize: 12,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--color-content-muted)',
            marginBottom: 6,
            fontWeight: 500,
        }}>
            {label}
        </div>
        {children}
        {hint ? (
            <div style={{ fontSize: 12, color: 'var(--color-content-muted)', marginTop: 6 }}>
                {hint}
            </div>
        ) : null}
    </label>
);

/** 텍스트 입력 — full-width, JetBrains Mono */
export const Input = ({ style, ...rest }) => (
    <input {...rest} style={{
        width: '100%',
        height: 38,
        padding: '0 12px',
        background: 'var(--color-surface-card-alt)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 8,
        color: 'var(--color-content)',
        fontSize: 13,
        fontFamily: 'JetBrains Mono, monospace',
        outline: 'none',
        ...style,
    }} />
);

/** 스파크라인 SVG — responsive 기본 (가로 100%) */
export const Spark = ({ data, w = 120, h = 36, tone = 'gain', fill = true, responsive = true }) => {
    const arr = Array.isArray(data) ? data : [];
    if (arr.length < 2) {
        return <svg width={responsive ? '100%' : w} height={h} aria-hidden="true" />;
    }
    const min = Math.min(...arr);
    const max = Math.max(...arr);
    const range = max - min || 1;
    const step = w / (arr.length - 1);
    const pts = arr.map((v, i) => [i * step, h - ((v - min) / range) * (h - 4) - 2]);
    const d = 'M' + pts.map(([x, y]) => `${x},${y}`).join(' L');
    const fillD = `${d} L${w},${h} L0,${h} Z`;
    const color = `var(--color-${tone})`;
    const svgProps = responsive
        ? { width: '100%', height: h, viewBox: `0 0 ${w} ${h}`, preserveAspectRatio: 'none' }
        : { width: w, height: h };
    return (
        <svg {...svgProps} style={{ display: 'block', maxWidth: '100%', overflow: 'visible' }} aria-hidden="true">
            {fill ? <path d={fillD} fill={color} opacity={0.12} /> : null}
            <path
                d={d}
                fill="none"
                stroke={color}
                strokeWidth={1.6}
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
            />
        </svg>
    );
};

/** 도넛 진행률 — value 0..100 */
export const Donut = ({ value, size = 80, stroke = 8, tone = 'brand', track = 'var(--color-surface-elevated)' }) => {
    const v = Math.max(0, Math.min(100, Number(value) || 0));
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const offset = c - (v / 100) * c;
    return (
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
            <circle cx={size / 2} cy={size / 2} r={r} stroke={track} strokeWidth={stroke} fill="none" />
            <circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                stroke={`var(--color-${tone})`}
                strokeWidth={stroke}
                fill="none"
                strokeLinecap="round"
                strokeDasharray={c}
                strokeDashoffset={offset}
            />
        </svg>
    );
};

/** 월별 미니 바 차트 — data: [{ v: number, ... }] · 0선 기준 양/음 색 분리 */
export const MiniBars = ({ data, w = 300, h = 60, gap = 3, responsive = true }) => {
    const arr = Array.isArray(data) ? data : [];
    if (arr.length === 0) {
        return <svg width={responsive ? '100%' : w} height={h} aria-hidden="true" />;
    }
    const max = Math.max(...arr.map(d => Math.abs(d.v))) || 1;
    const barW = (w - gap * (arr.length - 1)) / arr.length;
    const svgProps = responsive
        ? { width: '100%', height: h, viewBox: `0 0 ${w} ${h}`, preserveAspectRatio: 'none' }
        : { width: w, height: h };
    return (
        <svg {...svgProps} style={{ display: 'block', maxWidth: '100%' }} aria-hidden="true">
            {arr.map((d, i) => {
                const pct = Math.abs(d.v) / max;
                const bh = pct * (h - 4);
                const pos = d.v >= 0;
                const y = pos ? (h / 2) - bh : h / 2;
                return (
                    <rect
                        key={i}
                        x={i * (barW + gap)}
                        y={y}
                        width={barW}
                        height={bh}
                        rx={1.5}
                        fill={pos ? 'var(--color-gain)' : 'var(--color-loss)'}
                        opacity={0.85}
                    />
                );
            })}
            <line
                x1="0"
                y1={h / 2}
                x2={w}
                y2={h / 2}
                stroke="var(--color-surface-border)"
                strokeDasharray="2 3"
            />
        </svg>
    );
};
